import { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Title } from '@/components/Dropdown';
import Tag from '@/components/Tag';
import Text from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Tags } from '@/pages/AssignTopics/components/AttachmentsField';

const Relative = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const DropdownSelectButton = styled(Text)`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: 0.2s ease-out border-color;
  &:focus,
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
  &:disabled {
    cursor: not-allowed;
    &:focus,
    &:hover {
      border-color: ${({ theme }) => theme.colors.grayscale};
    }
  }
`;

export const DropdownBox = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 8px 4px;
  max-height: 368px;
  overflow-y: auto;
  left: 0;
  width: 100%;
  z-index: 1;
`;

export const DropdownButton = styled.button`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
  ${Text} {
    transition: color ease-out 0.2s;
    > span {
      color: ${({ theme }) => theme.colors.purple};
    }
  }
`;

const StyledTitle = styled(Title)`
  margin-bottom: 12px;
  padding: 0;
`;

export const StyledText = styled(Text)`
  transition: color ease-out 0.2s;
  &:hover,
  &:active {
    color: ${({ theme }) => theme.colors.black};
  }
  &:disabled {
    &:hover,
    &:active {
      color: ${({ theme }) => theme.colors.gray};
      cursor: not-allowed;
    }
  }
`;

const StyledTags = styled(Tags)`
  margin-bottom: 0;
  margin-top: 12px;
`;

interface Props {
  options?: string[];
  title: string;
  filter: boolean | string[];
  setFilter: React.Dispatch<React.SetStateAction<boolean | string[]>>;
}

const FreelancersFilterDropdown: FC<Props> = ({
  options,
  title,
  filter,
  setFilter,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const [values, setValues] = useState<string[]>(
    Array.isArray(filter) ? filter : [],
  );

  const ref = useRef<HTMLButtonElement>(null);
  useOnClickOutside(ref, () => setShowDropdown(false));

  const filteredOptions = options?.filter((option) => !values.includes(option));

  useEffect(() => {
    setFilter(values);
  }, [values, setFilter]);

  return (
    <Dropdown>
      <Relative>
        <StyledTitle $color="black" $type="smallBold">
          {title}
          <StyledText
            as="button"
            $color="gray"
            $type="extraSmall"
            disabled={!values.length}
            onClick={() => {
              setValues([]);
            }}
          >
            Clear
          </StyledText>
        </StyledTitle>
        <DropdownSelectButton
          ref={ref}
          disabled={!!!filteredOptions?.length}
          as="button"
          $type="smallBold"
          onClick={() => setShowDropdown((prev) => !prev)}
        >
          Select
          <svg
            width="8"
            height="6"
            viewBox="0 0 8 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M4 5.5L8 0.5H0L4 5.5Z" fill="currentColor" />
          </svg>
        </DropdownSelectButton>
        {showDropdown && (
          <DropdownBox>
            {filteredOptions?.map((option, index) => (
              <DropdownButton
                key={index}
                type="button"
                as="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setValues([...values, option]);
                  setShowDropdown(false);
                }}
              >
                <Text $color="gray" $type="captionSemibold">
                  {option}
                </Text>
              </DropdownButton>
            ))}
          </DropdownBox>
        )}
      </Relative>
      {!!values.length && (
        <StyledTags>
          <>
            {values.map((value) => {
              return (
                <Tag
                  key={value}
                  onDelete={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setValues((prev) =>
                      prev.filter((prevValue) => prevValue !== value),
                    );
                  }}
                >
                  {value}
                </Tag>
              );
            })}
          </>
        </StyledTags>
      )}
    </Dropdown>
  );
};

export default FreelancersFilterDropdown;
